/**
 * BR Image Gallery — Frontend (view.js)
 *
 * Handles:
 * - Gallery mode: lightbox with keyboard navigation + swipe
 * - Slider mode:  infinite loop, autoplay, fade/slide transitions, touch swipe
 * - Carousel mode: infinite loop, multi-thumbnail sliding
 */

( () => {
	'use strict';

	/* ════════════════════════════════════════════
	   Utilities
	   ════════════════════════════════════════════ */

	const $ = ( sel, ctx = document ) => ctx.querySelector( sel );
	const $$ = ( sel, ctx = document ) => [ ...ctx.querySelectorAll( sel ) ];

	/**
	 * Simple touch swipe detection.
	 */
	function addSwipe( el, { onLeft, onRight, threshold = 50 } ) {
		let startX = 0;
		let startY = 0;

		el.addEventListener( 'touchstart', ( e ) => {
			startX = e.changedTouches[ 0 ].screenX;
			startY = e.changedTouches[ 0 ].screenY;
		}, { passive: true } );

		el.addEventListener( 'touchend', ( e ) => {
			const dx = e.changedTouches[ 0 ].screenX - startX;
			const dy = e.changedTouches[ 0 ].screenY - startY;

			if ( Math.abs( dx ) > Math.abs( dy ) && Math.abs( dx ) > threshold ) {
				if ( dx < 0 && onLeft ) onLeft();
				if ( dx > 0 && onRight ) onRight();
			}
		}, { passive: true } );
	}

	/* ════════════════════════════════════════════
	   Initialize each gallery
	   ════════════════════════════════════════════ */

	function initGallery( container ) {
		const mode = container.dataset.mode;
		const sliderLayout = container.dataset.sliderLayout || 'single';
		const imageFit = container.dataset.imageFit || 'cover';
		const thumbBgColor = container.dataset.thumbBgColor || '';

		// Apply CSS variables to the inner container so var() picks them up.
		if ( mode === 'slider' && sliderLayout === 'multi' ) {
			const carousel = container.querySelector( '.br-gallery__carousel' );
			if ( carousel ) {
				carousel.style.setProperty( '--br-img-fit', imageFit );
				if ( thumbBgColor ) carousel.style.setProperty( '--br-thumb-bg', thumbBgColor );
			}
		} else if ( mode === 'gallery' ) {
			const grid = container.querySelector( '.br-gallery__grid' );
			if ( grid ) {
				grid.style.setProperty( '--br-img-fit', imageFit );
				if ( thumbBgColor ) grid.style.setProperty( '--br-thumb-bg', thumbBgColor );
			}
		}

		// Build images array from DOM elements
		let images = [];

		if ( mode === 'gallery' ) {
			const items = $$( '.br-gallery__item', container );
			images = items.map( ( item ) => {
				const img = $( 'img', item );
				const caption = $( '.br-gallery__caption', item );
				return {
					url: img ? img.src : '',
					alt: img ? img.alt : '',
					caption: caption ? caption.textContent : '',
				};
			} );
		} else if ( mode === 'slider' && sliderLayout === 'multi' ) {
			const items = $$( '.br-gallery__carousel-item', container );
			images = items.map( ( item ) => {
				const img = $( 'img', item );
				const caption = $( '.br-gallery__carousel-caption', item );
				return {
					url: img ? img.src : '',
					alt: img ? img.alt : '',
					caption: caption ? caption.textContent : '',
				};
			} );
		} else if ( mode === 'slider' ) {
			const slides = $$( '.br-gallery__slide', container );
			images = slides.map( ( slide ) => {
				const img = $( 'img', slide );
				const caption = $( '.br-gallery__slide-caption', slide );
				return {
					url: img ? img.src : '',
					alt: img ? img.alt : '',
					caption: caption ? caption.textContent : '',
				};
			} );
		}

		if ( ! images.length ) return;

		if ( mode === 'gallery' ) {
			initGalleryMode( container, images );
		} else if ( mode === 'slider' && sliderLayout === 'multi' ) {
			initCarouselMode( container, images );
		} else if ( mode === 'slider' ) {
			initSliderMode( container, images );
		}
	}

	/* ════════════════════════════════════════════
	   MODE GALERIE — Lightbox
	   ════════════════════════════════════════════ */

	function initGalleryMode( container, images ) {
		const useLightbox = container.dataset.lightbox === 'true';
		if ( ! useLightbox ) return;

		const lightbox    = $( '.br-gallery__lightbox', container );
		const lbImg       = $( '.br-gallery__lightbox-img', lightbox );
		const lbCaption   = $( '.br-gallery__lightbox-caption', lightbox );
		const lbCounter   = $( '.br-gallery__lightbox-counter', lightbox );
		const lbClose     = $( '.br-gallery__lightbox-close', lightbox );
		const lbPrev      = $( '.br-gallery__lightbox-nav.is-prev', lightbox );
		const lbNext      = $( '.br-gallery__lightbox-nav.is-next', lightbox );
		const items       = $$( '.br-gallery__item', container );

		if ( ! lightbox || ! items.length ) return;

		let currentIndex = 0;
		let isOpen = false;

		const show = ( index ) => {
			currentIndex = index;
			const img = images[ index ];
			if ( ! img ) return;

			lbImg.src = img.url;
			lbImg.alt = img.alt || '';

			if ( lbCaption ) {
				lbCaption.textContent = img.caption || '';
				lbCaption.hidden = ! img.caption;
			}
			if ( lbCounter ) {
				lbCounter.textContent = `${ index + 1 } / ${ images.length }`;
			}
		};

		const open = ( index ) => {
			show( index );
			lightbox.hidden = false;
			lightbox.classList.add( 'is-open' );
			document.body.style.overflow = 'hidden';
			isOpen = true;
			lbClose.focus();
		};

		const close = () => {
			lightbox.classList.remove( 'is-open' );
			lightbox.hidden = true;
			document.body.style.overflow = '';
			isOpen = false;
			items[ currentIndex ]?.focus();
		};

		const prev = () => {
			const idx = currentIndex <= 0 ? images.length - 1 : currentIndex - 1;
			show( idx );
		};

		const next = () => {
			const idx = currentIndex >= images.length - 1 ? 0 : currentIndex + 1;
			show( idx );
		};

		items.forEach( ( item, i ) => {
			item.addEventListener( 'click', () => open( i ) );
			item.addEventListener( 'keydown', ( e ) => {
				if ( e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					open( i );
				}
			} );
		} );

		lbClose?.addEventListener( 'click', close );
		lbPrev?.addEventListener( 'click', prev );
		lbNext?.addEventListener( 'click', next );

		lightbox.addEventListener( 'click', ( e ) => {
			if ( e.target === lightbox || e.target.classList.contains( 'br-gallery__lightbox-content' ) ) {
				close();
			}
		} );

		document.addEventListener( 'keydown', ( e ) => {
			if ( ! isOpen ) return;
			switch ( e.key ) {
				case 'Escape':
					close();
					break;
				case 'ArrowLeft':
					prev();
					break;
				case 'ArrowRight':
					next();
					break;
			}
		} );

		addSwipe( lightbox, {
			onLeft: next,
			onRight: prev,
		} );
	}

	/* ════════════════════════════════════════════
	   MODE SLIDER (single) — Infinite loop
	   ════════════════════════════════════════════ */

	function initSliderMode( container, images ) {
		const slider = $( '.br-gallery__slider', container );
		if ( ! slider ) return;

		const track      = $( '.br-gallery__slider-track', slider );
		const slides     = $$( '.br-gallery__slide', slider );
		const dots       = $$( '.br-gallery__slider-dot', slider );
		const prevBtn    = $( '.br-gallery__slider-nav.is-prev', slider );
		const nextBtn    = $( '.br-gallery__slider-nav.is-next', slider );
		const pauseBtn   = $( '.br-gallery__slider-pause', slider );
		const counterCur = $( '.br-gallery__slider-current', slider );

		const transition = container.dataset.sliderTransition || 'fade';
		const autoplay   = container.dataset.sliderAutoplay === 'true';
		const timer      = parseFloat( container.dataset.sliderTimer || 5 ) * 1000;

		let currentIndex = 0;
		let isHovered    = false;
		let isAnimating  = false;

		const totalSlides = slides.length;
		if ( totalSlides <= 0 ) return;

		/* ────────────────────────────────────────
		   FADE transition — already seamless loop
		   ──────────────────────────────────────── */

		if ( transition === 'fade' ) {
			// Single slide: just show it
			if ( totalSlides === 1 ) {
				slides[ 0 ].classList.add( 'is-active' );
				return;
			}

			const goTo = ( index ) => {
				currentIndex = ( ( index % totalSlides ) + totalSlides ) % totalSlides;
				slides.forEach( ( s, i ) => {
					s.classList.toggle( 'is-active', i === currentIndex );
				} );
				dots.forEach( ( d, i ) => d.classList.toggle( 'is-active', i === currentIndex ) );
				if ( counterCur ) counterCur.textContent = currentIndex + 1;
			};

			const goNext = () => goTo( currentIndex + 1 );
			const goPrev = () => goTo( currentIndex - 1 );

			const ap = setupAutoplay( {
				callback: () => { if ( ! isHovered ) goNext(); },
				timer,
				autoplay,
				pauseBtn,
				el: slider,
				onHover: ( h ) => { isHovered = h; },
			} );

			setupSliderEvents( slider, {
				goPrev: () => { goPrev(); ap.reset(); },
				goNext: () => { goNext(); ap.reset(); },
				goTo: ( idx ) => { goTo( idx ); ap.reset(); },
				togglePlay: ap.toggle,
				prevBtn, nextBtn, dots, pauseBtn,
			} );

			setupVisibilityObserver( slider, autoplay, ap );

			goTo( 0 );
			if ( autoplay ) ap.start();
			return;
		}

		/* ────────────────────────────────────────
		   SLIDE transition — infinite with clones
		   ──────────────────────────────────────── */

		// Ensure the slider clips overflowing slides
		slider.style.overflow = 'hidden';

		// With only 1 slide, no need for infinite loop
		if ( totalSlides === 1 ) {
			slides[ 0 ].classList.add( 'is-active' );
			return;
		}

		// Clone last slide → prepend, first slide → append
		const firstClone = slides[ 0 ].cloneNode( true );
		const lastClone  = slides[ totalSlides - 1 ].cloneNode( true );
		firstClone.classList.add( 'is-clone' );
		firstClone.classList.remove( 'is-active' );
		lastClone.classList.add( 'is-clone' );
		lastClone.classList.remove( 'is-active' );

		track.appendChild( firstClone );
		track.insertBefore( lastClone, track.firstChild );

		// All slides: [lastClone, slide0, ..., slideN-1, firstClone]
		const allSlides = $$( '.br-gallery__slide', track );
		const extTotal  = allSlides.length; // totalSlides + 2

		// Override track styling for translateX-based approach
		track.classList.add( 'is-infinite-slide' );
		track.style.display = 'flex';
		track.style.width   = `${ extTotal * 100 }%`;
		allSlides.forEach( ( s ) => {
			s.style.flex      = `0 0 ${ 100 / extTotal }%`;
			s.style.width     = `${ 100 / extTotal }%`;
			s.style.position  = 'relative';
			s.style.opacity   = '1';
			s.classList.remove(
				'is-active', 'is-slide-in-left', 'is-slide-in-right',
				'is-slide-out-left', 'is-slide-out-right'
			);
		} );

		// trackPos: index in the extended array. Real slide 0 → trackPos 1.
		let trackPos = 1;

		function setPosition( pos, animate ) {
			const pct = ( pos / extTotal ) * 100;
			track.style.transition = animate ? 'transform 0.5s ease' : 'none';
			track.style.transform  = `translateX(-${ pct }%)`;
		}

		function realIndex( pos ) {
			if ( pos <= 0 ) return totalSlides - 1;
			if ( pos > totalSlides ) return 0;
			return pos - 1;
		}

		function updateUI() {
			const idx = realIndex( trackPos );
			currentIndex = idx;
			dots.forEach( ( d, i ) => d.classList.toggle( 'is-active', i === idx ) );
			if ( counterCur ) counterCur.textContent = idx + 1;
		}

		function goNext() {
			if ( isAnimating ) return;
			isAnimating = true;
			trackPos++;
			setPosition( trackPos, true );
			updateUI();
		}

		function goPrev() {
			if ( isAnimating ) return;
			isAnimating = true;
			trackPos--;
			setPosition( trackPos, true );
			updateUI();
		}

		function goTo( realIdx ) {
			if ( isAnimating ) return;
			isAnimating = true;
			trackPos = realIdx + 1;
			setPosition( trackPos, true );
			updateUI();
		}

		// Snap to real slide when landing on a clone
		track.addEventListener( 'transitionend', ( e ) => {
			// Only react to the track's own transform transition
			if ( e.target !== track ) return;
			isAnimating = false;

			if ( trackPos <= 0 ) {
				trackPos = totalSlides;
				setPosition( trackPos, false );
			} else if ( trackPos > totalSlides ) {
				trackPos = 1;
				setPosition( trackPos, false );
			}
		} );

		const ap = setupAutoplay( {
			callback: () => { if ( ! isHovered ) goNext(); },
			timer,
			autoplay,
			pauseBtn,
			el: slider,
			onHover: ( h ) => { isHovered = h; },
		} );

		setupSliderEvents( slider, {
			goPrev: () => { goPrev(); ap.reset(); },
			goNext: () => { goNext(); ap.reset(); },
			goTo:   ( idx ) => { goTo( idx ); ap.reset(); },
			togglePlay: ap.toggle,
			prevBtn, nextBtn, dots, pauseBtn,
		} );

		setupVisibilityObserver( slider, autoplay, ap );

		// Init
		setPosition( trackPos, false );
		updateUI();
		if ( autoplay ) ap.start();
	}

	/* ════════════════════════════════════════════
	   CAROUSEL MODE (multi-thumbnail) — Infinite
	   ════════════════════════════════════════════ */

	function initCarouselMode( container, images ) {
		const carousel = $( '.br-gallery__carousel', container );
		if ( ! carousel ) return;

		const viewport  = $( '.br-gallery__carousel-viewport', carousel );
		const track     = $( '.br-gallery__carousel-track', carousel );
		const origItems = $$( '.br-gallery__carousel-item', carousel );
		const dots      = $$( '.br-gallery__slider-dot', carousel );
		const prevBtn   = $( '.br-gallery__slider-nav.is-prev', carousel );
		const nextBtn   = $( '.br-gallery__slider-nav.is-next', carousel );
		const pauseBtn  = $( '.br-gallery__slider-pause', carousel );

		const autoplay    = container.dataset.sliderAutoplay === 'true';
		const timer       = parseFloat( container.dataset.sliderTimer || 5 ) * 1000;
		const visibleSlides = parseInt( container.dataset.sliderVisibleSlides || 4, 10 );

		const totalItems = origItems.length;
		let isHovered    = false;
		let isAnimating  = false;
		let animTimeout  = null;

		function snapBack() {
			isAnimating = false;
			clearTimeout( animTimeout );
			if ( currentPos >= totalItems ) {
				currentPos -= totalItems;
				setTrackPos( currentPos, false );
			} else if ( currentPos < 0 ) {
				currentPos += totalItems;
				setTrackPos( currentPos, false );
			}
		}

		function safeSetAnimating( v ) {
			isAnimating = v;
			clearTimeout( animTimeout );
			if ( v ) animTimeout = setTimeout( snapBack, 500 );
		}

		if ( totalItems <= 0 ) return;

		/* ── Clone items for infinite loop ── */
		// We need enough clones on each side to fill at least one viewport + 1
		const clonesPerSide = Math.max( visibleSlides + 1, totalItems );

		// Prepend clones (cycling backwards through origItems)
		for ( let i = 0; i < clonesPerSide; i++ ) {
			const srcIdx = ( ( totalItems - 1 - ( i % totalItems ) ) + totalItems ) % totalItems;
			const clone = origItems[ srcIdx ].cloneNode( true );
			clone.classList.add( 'is-clone' );
			clone.removeAttribute( 'data-index' );
			track.insertBefore( clone, track.firstChild );
		}

		// Append clones (cycling forward through origItems)
		for ( let i = 0; i < clonesPerSide; i++ ) {
			const srcIdx = i % totalItems;
			const clone = origItems[ srcIdx ].cloneNode( true );
			clone.classList.add( 'is-clone' );
			clone.removeAttribute( 'data-index' );
			track.appendChild( clone );
		}

		const cloneCount = clonesPerSide;

		// Get ALL items (clones + originals) for sizing
		const allItems = $$( '.br-gallery__carousel-item', track );

		// Read thumb ratio setting
		const thumbRatio  = container.dataset.sliderThumbRatio || 'auto';
		const sliderHeight = parseInt( container.dataset.sliderHeight || 500, 10 );

		// Force track to be a non-wrapping flex row
		track.style.display    = 'flex';
		track.style.flexWrap   = 'nowrap';
		track.style.alignItems = 'stretch';

		// Logical position 0 = first real item (DOM offset = cloneCount)
		let currentPos   = 0;
		let perItemWidths = null; // null = uniform; array = variable widths (contain mode)

		function getResponsiveVisible() {
			const w = carousel.offsetWidth;
			if ( w < 480 ) return Math.min( 2, visibleSlides );
			if ( w < 768 ) return Math.min( 3, visibleSlides );
			return visibleSlides;
		}

		/**
		 * Compute the pixel width of a single carousel item.
		 * - When a ratio is set: width = sliderHeight × (ratioW / ratioH)
		 * - When ratio is auto:  width = viewport / visibleSlides
		 */
		function getItemWidth() {
			const v   = getResponsiveVisible();
			const gap = 8;

			if ( thumbRatio !== 'auto' ) {
				const parts = thumbRatio.split( '/' ).map( Number );
				const rw = parts[ 0 ] || 1;
				const rh = parts[ 1 ] || 1;
				return sliderHeight * ( rw / rh );
			}
			return ( viewport.offsetWidth - gap * ( v - 1 ) ) / v;
		}

		/**
		 * Compute the natural-ratio width for one item (contain mode only).
		 */
		function naturalWidth( item ) {
			const img = $( 'img', item );
			if ( img && img.naturalWidth && img.naturalHeight ) {
				return Math.max( 40, Math.round( sliderHeight * img.naturalWidth / img.naturalHeight ) );
			}
			return getItemWidth();
		}

		/**
		 * Apply pixel dimensions to every item in the track (originals + clones).
		 * In contain mode each item gets a width proportional to its image ratio.
		 */
		function sizeAllItems() {
			const gap   = 8;
			const itemH = sliderHeight;

			// Viewport clips to the configured height
			viewport.style.height   = `${ itemH }px`;
			viewport.style.overflow = 'hidden';

			// Build per-item width array for contain mode
			if ( imageFit === 'contain' ) {
				perItemWidths = allItems.map( ( item ) => naturalWidth( item ) );
			} else {
				perItemWidths = null;
			}

			allItems.forEach( ( item, idx ) => {
				const itemW = perItemWidths ? perItemWidths[ idx ] : getItemWidth();
				item.style.flex     = `0 0 ${ itemW }px`;
				item.style.width    = `${ itemW }px`;
				item.style.minWidth = `${ itemW }px`;
				item.style.maxWidth = `${ itemW }px`;
				item.style.height   = `${ itemH }px`;
				item.style.overflow = 'hidden';

				const img = $( 'img', item );
				if ( img ) {
					img.style.width     = '100%';
					img.style.height    = '100%';
					img.style.objectFit = imageFit;
					img.style.display   = 'block';
				}
			} );

			track.style.gap = `${ gap }px`;
		}

		function setTrackPos( logicalPos, animate ) {
			const gap    = 8;
			const domIdx = logicalPos + cloneCount;
			let offset;

			if ( perItemWidths ) {
				// Variable-width items: sum widths up to domIdx
				offset = 0;
				for ( let i = 0; i < domIdx; i++ ) {
					offset += ( perItemWidths[ i ] || getItemWidth() ) + gap;
				}
			} else {
				offset = domIdx * ( getItemWidth() + gap );
			}

			track.style.transition = animate ? 'transform 0.4s ease' : 'none';
			track.style.transform  = `translateX(-${ offset }px)`;
		}

		function updateDots() {
			const real = ( ( currentPos % totalItems ) + totalItems ) % totalItems;
			dots.forEach( ( d, i ) => d.classList.toggle( 'is-active', i === real ) );
		}

		function goTo( pos, animate = true ) {
			if ( isAnimating && animate ) return;
			if ( animate ) safeSetAnimating( true );
			currentPos = pos;
			setTrackPos( currentPos, animate );
			updateDots();
		}

		function goNext() { goTo( currentPos + 1 ); }
		function goPrev() { goTo( currentPos - 1 ); }

		// Snap when landing in clone zone
		track.addEventListener( 'transitionend', ( e ) => {
			if ( e.target !== track ) return;
			clearTimeout( animTimeout );
			snapBack();
		} );

		/* ── Autoplay ── */

		const ap = setupAutoplay( {
			callback: () => { if ( ! isHovered ) goNext(); },
			timer,
			autoplay,
			pauseBtn,
			el: carousel,
			onHover: ( h ) => { isHovered = h; },
		} );

		/* ── Events ── */

		prevBtn?.addEventListener( 'click', () => { goPrev(); ap.reset(); } );
		nextBtn?.addEventListener( 'click', () => { goNext(); ap.reset(); } );

		dots.forEach( ( dot ) => {
			dot.addEventListener( 'click', () => {
				goTo( parseInt( dot.dataset.index, 10 ) );
				ap.reset();
			} );
		} );

		pauseBtn?.addEventListener( 'click', ap.toggle );

		carousel.setAttribute( 'tabindex', '0' );
		carousel.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowLeft' )  { e.preventDefault(); goPrev(); ap.reset(); }
			if ( e.key === 'ArrowRight' ) { e.preventDefault(); goNext(); ap.reset(); }
			if ( e.key === ' ' )          { e.preventDefault(); ap.toggle(); }
		} );

		addSwipe( carousel, {
			onLeft:  () => { goNext(); ap.reset(); },
			onRight: () => { goPrev(); ap.reset(); },
		} );

		/* ── Resize ── */

		let resizeTimer;
		window.addEventListener( 'resize', () => {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( () => {
				sizeAllItems();
				goTo( currentPos, false );
			}, 150 );
		} );

		setupVisibilityObserver( carousel, autoplay, ap );

		sizeAllItems();
		goTo( 0, false );
		if ( autoplay ) ap.start();

		// In contain mode: re-size once images have loaded so natural ratios are known.
		if ( imageFit === 'contain' ) {
			const origImgs = origItems.map( ( item ) => $( 'img', item ) ).filter( Boolean );
			let pending = origImgs.filter( ( img ) => ! img.complete || ! img.naturalWidth ).length;
			if ( pending > 0 ) {
				origImgs.forEach( ( img ) => {
					if ( ! img.complete || ! img.naturalWidth ) {
						img.addEventListener( 'load', () => {
							pending--;
							if ( pending === 0 ) {
								sizeAllItems();
								setTrackPos( currentPos, false );
							}
						}, { once: true } );
					}
				} );
			}
		}
	}

	/* ════════════════════════════════════════════
	   Shared helpers
	   ════════════════════════════════════════════ */

	/**
	 * Reusable autoplay controller.
	 */
	function setupAutoplay( { callback, timer, autoplay, pauseBtn, el, onHover } ) {
		let intervalId = null;
		let isPlaying  = autoplay;

		function start() {
			if ( intervalId ) clearInterval( intervalId );
			intervalId = setInterval( callback, timer );
		}

		function stop() {
			if ( intervalId ) { clearInterval( intervalId ); intervalId = null; }
		}

		function reset() {
			if ( isPlaying ) start();
		}

		function toggle() {
			isPlaying = ! isPlaying;
			isPlaying ? start() : stop();
			if ( pauseBtn ) {
				const playIcon  = $( '.is-playing', pauseBtn );
				const pauseIcon = $( '.is-paused', pauseBtn );
				if ( playIcon )  playIcon.hidden  = ! isPlaying;
				if ( pauseIcon ) pauseIcon.hidden = isPlaying;
			}
		}

		el.addEventListener( 'mouseenter', () => onHover( true ) );
		el.addEventListener( 'mouseleave', () => onHover( false ) );

		return {
			start, stop, reset, toggle,
			isPlaying:   () => isPlaying,
			hasInterval: () => !! intervalId,
		};
	}

	/**
	 * Wire up slider navigation events (buttons, dots, keyboard, swipe).
	 */
	function setupSliderEvents( el, { goPrev, goNext, goTo, togglePlay, prevBtn, nextBtn, dots, pauseBtn } ) {
		prevBtn?.addEventListener( 'click', goPrev );
		nextBtn?.addEventListener( 'click', goNext );

		dots.forEach( ( dot ) => {
			dot.addEventListener( 'click', () => {
				goTo( parseInt( dot.dataset.index, 10 ) );
			} );
		} );

		if ( pauseBtn && togglePlay ) {
			pauseBtn.addEventListener( 'click', togglePlay );
		}

		el.setAttribute( 'tabindex', '0' );
		el.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowLeft' )  { e.preventDefault(); goPrev(); }
			if ( e.key === 'ArrowRight' ) { e.preventDefault(); goNext(); }
			if ( e.key === ' ' && togglePlay ) { e.preventDefault(); togglePlay(); }
		} );

		addSwipe( el, { onLeft: goNext, onRight: goPrev } );
	}

	/**
	 * Intersection Observer — pause autoplay when element is off-screen.
	 */
	function setupVisibilityObserver( el, autoplay, ap ) {
		if ( ! autoplay || ! ( 'IntersectionObserver' in window ) ) return;

		const observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					if ( ap.isPlaying() && ! ap.hasInterval() ) ap.start();
				} else {
					ap.stop();
				}
			} );
		}, { threshold: 0.3 } );
		observer.observe( el );
	}

	/* ════════════════════════════════════════════
	   Init all galleries on the page
	   ════════════════════════════════════════════ */

	function init() {
		$$( '.br-img-gallery' ).forEach( initGallery );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();